import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  isISODateTimeType,
  ISO_DATE_TIME_FORMAT,
  ISODateTimeType,
  Timestamp,
  toTimestamp,
  TypeGuard,
  type Timezone,
} from '@principle-theorem/shared';
import { createHash } from 'crypto';
import { isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';

export const PATIENT_COMMUNICATION_TYPE = 'patientCommunications';

export const PATIENT_COMMUNICATION_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Communications List',
      description: '',
      idPrefix: PATIENT_COMMUNICATION_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export enum CorePracticePatientCommunicationStatus {
  Error = 'Error',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Failed = 'Failed',
  Queued = 'Queued',
  Undelivered = 'Undelivered',
  Received = 'Received',
  Pending = 'Pending',
}

export interface ICorePracticePatientCommunication {
  date: ISODateTimeType;
  patientName: string;
  patientNumber: string;
  direction: 'Inbound' | 'Outbound';
  sender: string;
  receiver: string;
  status: CorePracticePatientCommunicationStatus;
  method: 'Sms';
  body: string;
}

export function isCorePracticePatientCommunication(
  item: unknown,
  logError: boolean
): item is ICorePracticePatientCommunication {
  return TypeGuard.interface<ICorePracticePatientCommunication>(
    {
      date: isISODateTimeType,
      patientName: isString,
      patientNumber: isString,
      direction: TypeGuard.nilOr(isString),
      sender: isString,
      receiver: isString,
      status: TypeGuard.enumValue(CorePracticePatientCommunicationStatus),
      method: isString,
      body: isString,
    },
    logError
  )(item);
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ICorePracticePatientCommunicationTranslations {
  date: Timestamp;
}

export interface ICorePracticePatientCommunicationFilters {
  patientNumber: string;
}

const PATIENT_COMMUNICATION_SOURCE_QUERY = `SELECT
  DISTINCT ON (
    date,
    no,
    direction,
    status,
    body
  )
  to_char(to_timestamp(
    date,
    'DD/Mon/YYYY HH24:MI'
  )::timestamp, 'YYYY-MM-DD HH24:MI:SS') as date,
  patient AS patient_name,
  convert_to_text(no) as patient_number,
  direction,
  convert_to_text(sender) AS sender,
  convert_to_text(receiver) AS receiver,
  status,
  method,
  body
FROM communication
WHERE no IS NOT NULL`;

export type CorePracticePatientCommunication = ISourceEntityTypes<
  ICorePracticePatientCommunication,
  ICorePracticePatientCommunicationTranslations,
  ICorePracticePatientCommunicationFilters
>;

export class PatientCommunicationSourceEntity extends BaseSourceEntity<CorePracticePatientCommunication> {
  sourceEntity = PATIENT_COMMUNICATION_SOURCE_ENTITY;
  entityResourceType = PATIENT_COMMUNICATION_TYPE;
  sourceQuery = PATIENT_COMMUNICATION_SOURCE_QUERY;
  verifySourceFn = isCorePracticePatientCommunication;

  translate(
    data: ICorePracticePatientCommunication,
    timezone: Timezone
  ): ICorePracticePatientCommunicationTranslations {
    return {
      date: toTimestamp(moment.tz(data.date, ISO_DATE_TIME_FORMAT, timezone)),
    };
  }

  getSourceRecordId(data: ICorePracticePatientCommunication): string {
    const hash = createHash('SHA-256');
    hash.update(data.body);
    return `${data.patientNumber} - ${data.date} - ${data.direction} - ${
      data.status
    } - ${hash.digest('hex')}`;
  }

  getSourceLabel(data: ICorePracticePatientCommunication): string {
    return `${data.patientName} - ${data.date} - ${data.direction} - ${data.status}`;
  }

  getFilterData(
    data: ICorePracticePatientCommunication,
    _timezone: Timezone
  ): ICorePracticePatientCommunicationFilters {
    return {
      patientNumber: data.patientNumber,
    };
  }
}
